/**
 * InfinitSales - InboxLayout (frente C1, flag `persistent_layout`)
 *
 * Pai das rotas /inbox, /wa-business, /instagram, /messenger, /widget e /funis. Com a
 * flag ligada, a NavSidebar (e com ela o status dos canais Instagram e
 * Messenger) vive AQUI e nao remonta ao trocar de rota: so o shell do canal
 * (o <Outlet/>) troca. Antes, cada troca desmontava a tela inteira e remontava
 * com ~12 requisicoes (medido em 22/09).
 *
 * Com a flag desligada renderiza so o <Outlet/>: os shells seguem montando a
 * propria NavSidebar, byte a byte como hoje.
 *
 * Como os shells falam com a NavSidebar do layout: cada shell publica as props
 * (currentPage, onPageChange, onRefresh, onOpenConnection, isRefreshing,
 * mobileHideNav) por `setNavProps` no contexto. Os handlers entram num ref (a
 * NavSidebar recebe wrappers estaveis); so as props primitivas causam
 * re-render, e o valor do contexto e estavel, entao publicar nao re-renderiza
 * o shell (sem laco).
 *
 * O SSEProvider ja e global (App.js) e nao remonta por rota; o sino fica na
 * barra da lista de cada shell porque sua posicao e dentro da coluna de 395px,
 * e o estado dele mora no NotificationsContext (global), entao remontar o
 * componente custa zero requisicoes.
 *
 * Tambem monta o prefetch dos outros canais (frente C3, flag `inbox_prefetch`)
 * e o marcador de "cache velho" do inboxStore para canais sem shell montado.
 */
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useSSEEvent, SSE_EVENTS } from '../../contexts/SSEContext';
import useFeatureFlags from '../../hooks/useFeatureFlags';
import NavSidebar from '../NavSidebar';
import useInboxPrefetch from '../../services/inbox/useInboxPrefetch';
import { isInboxChannelMounted, markInboxStale, INBOX_CHANNELS } from '../../services/inbox/inboxStore';

const InboxLayoutContext = createContext({
  layoutOwnsNav: false,
  setNavProps: () => {},
});

/**
 * Lido pelos shells: `layoutOwnsNav` diz se a NavSidebar ja esta no layout
 * (o shell entao nao renderiza a sua) e `setNavProps` publica as props.
 */
export function useInboxLayout() {
  return useContext(InboxLayoutContext);
}

function channelFromPath(pathname) {
  if (pathname.startsWith('/wa-business')) return 'wa_business';
  if (pathname.startsWith('/instagram')) return 'instagram';
  if (pathname.startsWith('/messenger')) return 'messenger';
  if (pathname.startsWith('/widget')) return 'widget';
  if (pathname.startsWith('/inbox')) return 'wa_personal';
  return null;
}

// NavSidebar com handlers estaveis: le o ref na hora do clique.
const LayoutNav = React.memo(function LayoutNav({ primitives, handlersRef }) {
  return (
    <NavSidebar
      currentPage={primitives.currentPage}
      isRefreshing={primitives.isRefreshing}
      mobileHideNav={primitives.mobileHideNav}
      onPageChange={(page) => handlersRef.current.onPageChange?.(page)}
      onRefresh={() => handlersRef.current.onRefresh?.()}
      onOpenConnection={() => handlersRef.current.onOpenConnection?.()}
    />
  );
});

const InboxLayout = () => {
  const { isEnabled } = useFeatureFlags();
  const { isAuthenticated, loading } = useAuth();
  const { colors } = useTheme();
  const location = useLocation();
  const currentChannel = channelFromPath(location.pathname);

  // Frente C3: prefetch dos outros canais (a flag e lida dentro do hook).
  useInboxPrefetch(isAuthenticated && !loading ? currentChannel : null);

  // Frente C2: mensagem nova num canal cujo shell NAO esta montado deixa o
  // cache daquele canal marcado como velho (a proxima montagem pinta o cache e
  // revalida na hora). O shell montado ja aplica o patch pelos handlers dele.
  useSSEEvent(SSE_EVENTS.NEW_MESSAGE, (data) => {
    const source = data?.source || 'wa_personal';
    if (!INBOX_CHANNELS.includes(source)) return;
    if (isInboxChannelMounted(source)) return;
    markInboxStale(source);
  }, []);

  const persistent = isEnabled('persistent_layout') && isAuthenticated && !loading;

  const [primitives, setPrimitives] = useState({
    currentPage: currentChannel === 'wa_business' || currentChannel === 'wa_personal' ? 'inbox' : (currentChannel || 'inbox'),
    isRefreshing: false,
    mobileHideNav: false,
  });
  const handlersRef = useRef({});

  const setNavProps = useCallback((props) => {
    const {
      onPageChange, onRefresh, onOpenConnection,
      currentPage, isRefreshing = false, mobileHideNav = false,
    } = props || {};
    handlersRef.current = { onPageChange, onRefresh, onOpenConnection };
    setPrimitives((prev) => {
      const next = {
        currentPage: currentPage ?? prev.currentPage,
        isRefreshing: Boolean(isRefreshing),
        mobileHideNav: Boolean(mobileHideNav),
      };
      if (prev.currentPage === next.currentPage
        && prev.isRefreshing === next.isRefreshing
        && prev.mobileHideNav === next.mobileHideNav) return prev;
      return next;
    });
  }, []);

  const ctx = useMemo(
    () => ({ layoutOwnsNav: persistent, setNavProps }),
    [persistent, setNavProps],
  );

  // Mesma altura que os shells usam: 100dvh no celular acompanha a barra do
  // navegador; no desktop 100vh. A NavSidebar vira barra fixa no celular, e
  // o shell continua ocupando a largura toda.
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' && window.innerWidth < 768,
  );
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // A ARVORE e a mesma nos dois modos, de proposito. A flag chega por fetch
  // depois do primeiro render (boot otimista), e trocar `<Outlet/>` direto
  // por `<div><Nav/><div><Outlet/></div></div>` mudava a posicao do shell na
  // arvore: o React desmontava e remontava o inbox, refazendo todas as
  // requisicoes da montagem (status, chats, autopilot-config em dobro, medido
  // em 22/09). Com a flag OFF os dois wrappers ficam com `display: contents`
  // (nao geram caixa) e o slot da NavSidebar fica vazio: layout identico ao
  // de hoje, sem remontagem quando a flag liga.
  const wrapperStyle = persistent
    ? {
      display: 'flex',
      width: isMobile ? '100%' : '100vw',
      height: isMobile ? '100dvh' : '100vh',
      overflow: 'hidden',
      backgroundColor: colors.bgSecondary,
    }
    : { display: 'contents' };
  const innerStyle = persistent
    ? { flex: 1, minWidth: 0, display: 'flex', overflow: 'hidden' }
    : { display: 'contents' };

  return (
    <InboxLayoutContext.Provider value={ctx}>
      <div style={wrapperStyle}>
        {persistent ? <LayoutNav primitives={primitives} handlersRef={handlersRef} /> : null}
        <div style={innerStyle}>
          <Outlet />
        </div>
      </div>
    </InboxLayoutContext.Provider>
  );
};

export default InboxLayout;
